import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Lock, AlertCircle, Calendar, User, Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_SERVER_BASE_URL; // 백엔드 포트에 맞게 수정

function App() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(''); // SUCCESS, AUTH_REQUIRED, SHARE_CANCELLED, NOT_FOUND
  const [password, setPassword] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const link = window.location.pathname.split('/')[1]; // URL에서 링크값 추출

const STORAGE_KEY = `auth_pwd_${link}`;

  const fetchData = async (inputPassword = '') => {
    try {
      // 인자로 넘어온 게 없으면 세션에서 가져와봄
      const targetPassword = inputPassword || sessionStorage.getItem(STORAGE_KEY) || '';
      
      setLoading(!targetPassword); // 저장된 비번이 없으면 전체 로딩, 있으면 조용히 요청
      setErrorMsg('');

      const response = await axios.get(`${API_BASE}/${link}`, {
        params: { pwd: targetPassword }
      });
      
      const { success, result_code, data: mailData } = response.data;
      
      if (success) {
        setStatus(result_code);
        setData(mailData);
        // 💡 성공 시 세션 저장소에 비밀번호 저장
        if (targetPassword) {
          sessionStorage.setItem(STORAGE_KEY, targetPassword);
        }
      }
    } catch (err) {
      if (err.response) {
        const { result_code } = err.response.data;
        setStatus(result_code);
        if (result_code === 'AUTH_REQUIRED' && inputPassword !== '') {
          setErrorMsg('비밀번호가 올바르지 않습니다.');
          // 💡 틀렸다면 저장되어 있던 잘못된 비번 삭제
          sessionStorage.removeItem(STORAGE_KEY);
        }
      } else {
        setStatus('SERVER_ERROR');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    if (!link || link.trim() === "") {
      setStatus('INVALID_ACCESS');
      setLoading(false);
      return;
    }

    fetchData();
  }, [link]); // link가 바뀔 때만 다시 실행되도록 추가

  if (loading) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-3">
        <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
        <p className="text-sm font-medium text-slate-600">메일을 불러오는 중입니다.</p>
      </div>
    );
  }

  if (status === 'INVALID_ACCESS' || status === 'SERVER_ERROR') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="mx-auto text-orange-400 mb-4" size={64} />
          <h2 className="text-2xl font-bold text-gray-800">잘못된 접근입니다</h2>
          <p className="text-gray-500 mt-2">
            {status === 'INVALID_ACCESS' 
              ? '링크를 다시 확인후 이용해주세요.' 
              : '서버와 연결이 원활하지 않습니다. 잠시 후 다시 시도해주세요.'}
          </p>
        </div>
      </div>
    );
  }

  // 1. 비밀번호 인증 UI (AUTH_REQUIRED)
  if (status === 'AUTH_REQUIRED') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="text-indigo-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">보호된 메일입니다</h2>
          <p className="text-gray-500 mb-6">링크와 함께 공유된 비밀번호를 입력해주세요.</p>
          
          <input 
            type="password" 
            className={`w-full px-4 py-3 border rounded-lg outline-none transition focus:ring-2 ${
              errorMsg ? 'border-red-500 focus:ring-red-200' : 'border-gray-300 focus:ring-indigo-500'
            }`}
            placeholder="비밀번호 입력"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchData(password)} // 엔터키 지원
          />
          
          {/* 에러 메시지 표시 */}
          {errorMsg && <p className="text-red-500 text-sm mt-2 text-left">{errorMsg}</p>}
          <button 
            onClick={() => fetchData(password)} // 💡 버튼 클릭 시 fetchData 실행
            className="w-full mt-4 bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition"
          >
            확인
          </button>
        </div>
      </div>
    );
  }

  // 2. 공유 취소 또는 없음 (SHARE_CANCELLED, NOT_FOUND)
  if (status === 'SHARE_CANCELLED' || status === 'NOT_FOUND') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={64} />
          <h2 className="text-2xl font-bold text-gray-800">접근할 수 없는 링크입니다</h2>
          <p className="text-gray-500 mt-2">{status === 'SHARE_CANCELLED' ? '공유가 종료된 메일 입니다.' : '링크가 유효하지 않습니다.'}</p>
        </div>
      </div>
    );
  }

  // 3. 메일 본문 UI (SUCCESS)
  if (status === 'SUCCESS') {
    return (
      <div className="min-h-screen bg-gray-100 py-12 px-4">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm overflow-hidden">
          
          {/* 💡 상단 알림 배너 추가: 공유된 메일임을 강조 */}
          <div className="bg-indigo-50 border-b border-indigo-100 px-8 py-3 flex items-center justify-between">
            <div className="flex items-center text-indigo-700 text-sm font-medium">
              <AlertCircle size={16} className="mr-2" />
              본 메일은 안전하게 공유된 메일 입니다.
            </div>
          </div>
  
          {/* 헤더 섹션 */}
          <div className="p-8 border-b border-gray-100 bg-white">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-6">{data?.title}</h1>
            <div className="space-y-3 text-sm text-gray-600">
              <div className="flex items-center"><User size={16} className="mr-2"/> <b>보낸 사람:</b> {data?.from}</div>
              <div className="flex items-center"><Mail size={16} className="mr-2"/> <b>받는 사람:</b> {data?.to}</div>
              <div className="flex items-center text-gray-400"><Calendar size={16} className="mr-2"/> {data?.reception_time && new Date(Number(data?.reception_time) * 1000).toLocaleString()}</div>
            </div>
          </div>
  
          {/* 본문 섹션 */}
          <div className="p-8 prose max-w-none text-gray-800 leading-relaxed border-b border-gray-50">
            <div dangerouslySetInnerHTML={{ __html: data?.content }} />
          </div>
  
          {/* 💡 하단 경고 섹션 추가: 재배포 금지 강조 */}
          <div className="p-6 bg-gray-50 border-t border-gray-100">
            <div className="flex items-start space-x-3 text-gray-500">
              <Lock size={18} className="mt-0.5 flex-shrink-0 text-red-400" />
              <div className="text-xs leading-5">
                <p className="font-semibold text-gray-700 mb-1 text-sm underline decoration-red-200 underline-offset-4">보안 및 재배포 안내</p>
                <p>• 이 링크는 특정 사용자에게만 제공된 보안 링크입니다.</p>
                <p className="text-red-500 font-medium">• 본 메일 내용 및 링크를 <b>제3자에게 무단으로 공유, 재배포, 복제</b>하는 행위는 엄격히 금지됩니다.</p>
                <p>• 비정상적인 접근이 감지될 경우 서비스 이용이 제한될 수 있습니다.</p>
              </div>
            </div>
          </div>
  
        </div>
  
        {/* 푸터 영역 (선택사항) */}
        <p className="text-center text-gray-400 text-xs mt-8">
          &copy; {new Date().getFullYear()} GLIM All rights reserved.
        </p>
      </div>
    );
  }
}

export default App;